/**
 * Bulk Document Upload Component
 * Allows agents to upload multiple documents that get auto-matched to cases
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, FileText, CheckCircle, XCircle, AlertTriangle, 
  Search, Link2, Trash2, RefreshCw, Check, X, Download,
  Folder, File, Image, FileSpreadsheet, Briefcase, GraduationCap,
  MapPin, Gavel, CreditCard, Users, TestTube, Shield, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check type options with icons
const CHECK_TYPE_OPTIONS = [
  { value: 'all', label: 'All Check Types (Auto-detect)', icon: FileText },
  { value: 'employment_check', label: 'Employment Verification', icon: Briefcase },
  { value: 'education_check', label: 'Education Verification', icon: GraduationCap },
  { value: 'address_check', label: 'Address Verification', icon: MapPin },
  { value: 'criminal_court_check', label: 'Criminal / Court Records', icon: Gavel },
  { value: 'id_check', label: 'ID / PAN / Aadhar Verification', icon: CreditCard },
  { value: 'reference_check', label: 'Reference Check', icon: Users },
  { value: 'drug_test', label: 'Drug Test', icon: TestTube },
  { value: 'credit_check', label: 'Credit Check', icon: Shield },
];

const BulkDocumentUpload = ({ onClose, preselectedCheckType = null, preselectedCaseId = null }) => {
  const [sessionId, setSessionId] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [step, setStep] = useState('upload'); // upload, review, commit
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFileForMatch, setSelectedFileForMatch] = useState(null);
  const [matchedCount, setMatchedCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  
  // New state for check type selection
  const [selectedCheckType, setSelectedCheckType] = useState(preselectedCheckType || 'all');
  const [selectedCase, setSelectedCase] = useState(preselectedCaseId || null);
  const [caseDetails, setCaseDetails] = useState(null);

  // Get token
  const getToken = () => localStorage.getItem('token');

  // Fetch case details if preselected
  useEffect(() => {
    if (preselectedCaseId) {
      fetchCaseDetails(preselectedCaseId);
    }
  }, [preselectedCaseId]);

  const fetchCaseDetails = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/search?q=${caseId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      if (data.length > 0) {
        setCaseDetails(data.find(c => c.id === caseId) || data[0]);
        setSelectedCase(caseId);
      }
    } catch (error) {
      console.error('Error fetching case details:', error);
    }
  };

  // Initialize upload session
  const initSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bulk-upload/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.session_id) {
        setSessionId(data.session_id);
        return data.session_id;
      }
    } catch (error) {
      console.error('Error initiating session:', error);
      toast.error('Failed to start upload session');
    }
    return null;
  };

  // Dropzone configuration
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    
    try {
      // Initialize session if not exists
      let sid = sessionId;
      if (!sid) {
        sid = await initSession();
        if (!sid) {
          setUploading(false);
          return;
        }
      }

      // Upload files
      const formData = new FormData();
      acceptedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/api/bulk-upload/${sid}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.files) {
        setFiles(prev => [...prev, ...data.files]);
        toast.success(`Uploaded ${data.files.length} files`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  }, [sessionId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: true
  });

  // Analyze documents
  const analyzeDocuments = async () => {
    if (!sessionId || files.length === 0) return;

    setAnalyzing(true);
    try {
      // Build query params for check type and case filtering
      let url = `${API_URL}/api/bulk-upload/${sessionId}/analyze`;
      const params = new URLSearchParams();
      if (selectedCheckType && selectedCheckType !== 'all') {
        params.append('check_type', selectedCheckType);
      }
      if (selectedCase) {
        params.append('case_id', selectedCase);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      // Read as text first so we can surface non-JSON responses (e.g. an
      // HTML 500/504 page from the ingress when the pod is slow). Previously
      // `response.json().catch(() => ({}))` silently swallowed the body, leaving
      // only "HTTP 500" visible in the toast which was useless for debugging
      // production-only failures.
      const rawText = await response.text();
      let data = {};
      try { data = rawText ? JSON.parse(rawText) : {}; } catch (_jerr) { data = {}; }
      if (!response.ok) {
        const detail = (data && data.detail)
          || (rawText ? rawText.slice(0, 400).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '')
          || `HTTP ${response.status}`;
        console.error('[BulkUpload analyze]', response.status, detail, { rawText, data });
        toast.error(`Analyze failed: ${detail}`, { duration: 12000 });
        return;
      }
      if (data.files) {
        setFiles(data.files);
        setMatchedCount(data.matched_count || 0);
        setUnmatchedCount(data.unmatched_count || 0);
        setStep('review');
        toast.success(`Analyzed ${data.files.length} files. ${data.matched_count} matched automatically.`);
      } else {
        toast.error('Analyze succeeded but returned no files');
      }
    } catch (error) {
      console.error('Error analyzing documents:', error);
      toast.error(`Failed to analyze documents: ${error.message || 'network error'}`, { duration: 10000 });
    } finally {
      setAnalyzing(false);
    }
  };

  // AI Analyze documents
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  
  const aiAnalyzeDocuments = async () => {
    if (!sessionId || files.length === 0) return;

    setAiAnalyzing(true);
    try {
      const response = await fetch(`${API_URL}/api/bulk-upload/${sessionId}/ai-analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const rawText = await response.text();
      let data = {};
      try { data = rawText ? JSON.parse(rawText) : {}; } catch (_jerr) { data = {}; }
      if (!response.ok) {
        const detail = (data && data.detail)
          || (rawText ? rawText.slice(0, 400).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '')
          || `HTTP ${response.status}`;
        console.error('[BulkUpload ai-analyze]', response.status, detail, { rawText, data });
        toast.error(`AI analysis failed: ${detail}`, { duration: 12000 });
        return;
      }
      if (data.files) {
        setFiles(data.files);
        setAiResults({
          clear: data.clear_count || 0,
          discrepancy: data.discrepancy_count || 0,
          pending: data.pending_review_count || 0
        });
        toast.success(`AI Analysis Complete: ${data.clear_count} Clear, ${data.discrepancy_count} Discrepancy`);
      }
    } catch (error) {
      console.error('Error in AI analysis:', error);
      toast.error('Failed to perform AI analysis');
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Auto-update checks from AI analysis
  const [autoUpdating, setAutoUpdating] = useState(false);
  
  const autoUpdateChecks = async () => {
    if (!sessionId) return;

    setAutoUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/bulk-upload/${sessionId}/auto-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      toast.success(`Auto-updated ${data.updated_checks?.length || 0} verification checks`);
      setStep('complete');
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} checks had errors`);
      }
    } catch (error) {
      console.error('Error auto-updating checks:', error);
      toast.error('Failed to auto-update checks');
    } finally {
      setAutoUpdating(false);
    }
  };

  // Search cases for manual matching
  const searchCases = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/cases/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching cases:', error);
    }
  };

  // Manual match document
  const matchDocument = async (fileId, caseId, checkId = null) => {
    try {
      const response = await fetch(`${API_URL}/api/bulk-upload/${sessionId}/match?file_id=${fileId}&case_id=${caseId}${checkId ? `&check_id=${checkId}` : ''}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.file) {
        setFiles(prev => prev.map(f => f.id === fileId ? data.file : f));
        setMatchedCount(prev => prev + 1);
        setUnmatchedCount(prev => Math.max(0, prev - 1));
        setSelectedFileForMatch(null);
        setSearchQuery('');
        setSearchResults([]);
        toast.success('Document matched successfully');
      }
    } catch (error) {
      console.error('Error matching document:', error);
      toast.error('Failed to match document');
    }
  };

  // Commit upload
  const commitUpload = async () => {
    if (!sessionId) return;

    setCommitting(true);
    try {
      const response = await fetch(`${API_URL}/api/bulk-upload/${sessionId}/commit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      toast.success(`Successfully attached ${data.committed_files?.length || 0} documents to cases`);
      setStep('complete');
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} files had errors`);
      }
    } catch (error) {
      console.error('Error committing upload:', error);
      toast.error('Failed to commit upload');
    } finally {
      setCommitting(false);
    }
  };

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Get file icon
  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  // Get confidence color based on score
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return { bg: 'bg-green-500', text: 'text-green-700', border: 'border-green-300', light: 'bg-green-100' };
    if (confidence >= 0.7) return { bg: 'bg-amber-500', text: 'text-amber-700', border: 'border-amber-300', light: 'bg-amber-100' };
    if (confidence >= 0.5) return { bg: 'bg-orange-500', text: 'text-orange-700', border: 'border-orange-300', light: 'bg-orange-100' };
    return { bg: 'bg-red-500', text: 'text-red-700', border: 'border-red-300', light: 'bg-red-100' };
  };

  // Get confidence label
  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    if (confidence >= 0.5) return 'Low';
    return 'Very Low';
  };

  // Get match status badge with confidence indicator
  const getMatchBadge = (file) => {
    if (!file.case_id) {
      return <Badge className="bg-red-100 text-red-700 border border-red-300">Unmatched</Badge>;
    }
    
    const confidence = file.match_confidence || 0;
    const colors = getConfidenceColor(confidence);
    const label = getConfidenceLabel(confidence);
    const percentage = Math.round(confidence * 100);
    
    // Manual match (confidence = 1.0 exactly from user action)
    if (confidence === 1.0) {
      return <Badge className="bg-blue-100 text-blue-700 border border-blue-300">Manual Match</Badge>;
    }
    
    return (
      <div className="flex items-center gap-2">
        <Badge className={`${colors.light} ${colors.text} border ${colors.border}`}>
          {label} Match
        </Badge>
        <div className="flex items-center gap-1">
          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.bg} transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className={`text-xs font-semibold ${colors.text}`}>{percentage}%</span>
        </div>
      </div>
    );
  };

  // Confidence indicator for the review step
  const ConfidenceIndicator = ({ confidence }) => {
    const colors = getConfidenceColor(confidence);
    const percentage = Math.round(confidence * 100);
    const label = getConfidenceLabel(confidence);
    
    return (
      <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${colors.light} border ${colors.border}`}>
        <div className="relative w-8 h-8">
          <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${percentage * 0.94} 100`}
              className={colors.text}
            />
          </svg>
          <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${colors.text}`}>
            {percentage}
          </span>
        </div>
        <div>
          <p className={`text-xs font-semibold ${colors.text}`}>{label}</p>
          <p className="text-[10px] text-slate-500">Confidence</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Bulk Document Upload</h2>
          <p className="text-sm text-slate-500">Upload multiple documents and automatically match them to cases</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {files.length} Files
          </Badge>
          {step !== 'upload' && (
            <>
              <Badge className="bg-green-100 text-green-700">{matchedCount} Matched</Badge>
              <Badge className="bg-red-100 text-red-700">{unmatchedCount} Unmatched</Badge>
            </>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'upload' ? 'bg-blue-600 text-white' : 'bg-green-100 text-green-700'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</span>
          Upload
        </div>
        <div className="w-12 h-1 bg-slate-200"></div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'review' ? 'bg-blue-600 text-white' : step === 'complete' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</span>
          Review & Match
        </div>
        <div className="w-12 h-1 bg-slate-200"></div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'complete' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
          <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">3</span>
          Complete
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          {/* Check Type and Case Selection */}
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-blue-800 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Document Matching Options
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              {/* Check Type Selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Target Check Type</Label>
                <Select value={selectedCheckType} onValueChange={setSelectedCheckType}>
                  <SelectTrigger className="h-10 bg-white">
                    <SelectValue placeholder="Select check type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CHECK_TYPE_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-500" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {selectedCheckType === 'all' 
                    ? 'Documents will be matched to any check type based on filename' 
                    : `All documents will be matched to ${CHECK_TYPE_OPTIONS.find(o => o.value === selectedCheckType)?.label} checks only`}
                </p>
              </div>
              
              {/* Case Selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Target Case (Optional)</Label>
                <div className="relative">
                  <Input 
                    placeholder="Search by Employee Name or Case ID..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (e.target.value.length >= 2) {
                        searchCases(e.target.value);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    className="h-10 bg-white"
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchResults.map(caseItem => (
                        <div 
                          key={caseItem.id}
                          className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setSelectedCase(caseItem.id);
                            setCaseDetails(caseItem);
                            setSearchQuery(caseItem.employee_name);
                            setSearchResults([]);
                          }}
                        >
                          <p className="font-medium text-sm">{caseItem.employee_name}</p>
                          <p className="text-xs text-slate-500">{caseItem.client_name} | {caseItem.id}...</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {caseDetails && (
                  <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-green-700">{caseDetails.employee_name}</p>
                      <p className="text-xs text-green-600">{caseDetails.checks?.length || 0} checks</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setSelectedCase(null); setCaseDetails(null); setSearchQuery(''); }}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  {selectedCase 
                    ? 'All documents will be matched to this case' 
                    : 'Leave empty to auto-detect cases from filenames'}
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Show selected check type badge */}
          {(selectedCheckType !== 'all' || selectedCase) && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Filtered Upload Mode</p>
                <p className="text-xs text-amber-600">
                  {selectedCheckType !== 'all' && `Check Type: ${CHECK_TYPE_OPTIONS.find(o => o.value === selectedCheckType)?.label}`}
                  {selectedCheckType !== 'all' && selectedCase && ' | '}
                  {selectedCase && `Case: ${caseDetails?.employee_name || selectedCase.slice(0, 8)}`}
                </p>
              </div>
            </div>
          )}
          
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-blue-500' : 'text-slate-400'}`} />
            {isDragActive ? (
              <p className="text-blue-600 font-medium">Drop the files here...</p>
            ) : (
              <>
                <p className="text-slate-600 font-medium">Drag & drop files here, or click to select</p>
                <p className="text-sm text-slate-400 mt-2">
                  Supports PDF, Images, Word, Excel files
                </p>
              </>
            )}
          </div>

          {/* Uploaded files list */}
          {files.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Uploaded Files ({files.length})</CardTitle>
              </CardHeader>
              <CardContent className="max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {files.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.mime_type)}
                        <div>
                          <p className="text-sm font-medium truncate max-w-xs">{file.original_filename}</p>
                          <p className="text-xs text-slate-500">{(file.file_size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(file.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={analyzeDocuments} 
              disabled={files.length === 0 || analyzing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {analyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Analyze & Match ({files.length} files)
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Review & Match */}
      {step === 'review' && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">High Confidence (≥90%)</p>
                  <p className="text-2xl font-bold text-green-700">
                    {files.filter(f => f.case_id && f.match_confidence >= 0.9).length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600">Medium Confidence (70-89%)</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {files.filter(f => f.case_id && f.match_confidence >= 0.7 && f.match_confidence < 0.9).length}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600">Needs Review / Unmatched</p>
                  <p className="text-2xl font-bold text-red-700">
                    {files.filter(f => !f.case_id || f.match_confidence < 0.7).length}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </CardContent>
            </Card>
          </div>

          {/* AI Analysis Section */}
          {matchedCount > 0 && (
            <Card className="border-2 border-purple-200 bg-purple-50/50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-800">AI Document Analysis</h4>
                      <p className="text-sm text-purple-600">
                        Automatically analyze documents to determine Clear/Discrepancy status
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {aiResults && (
                      <div className="flex gap-2 mr-4">
                        <Badge className="bg-green-100 text-green-700">{aiResults.clear} Clear</Badge>
                        <Badge className="bg-red-100 text-red-700">{aiResults.discrepancy} Discrepancy</Badge>
                        <Badge className="bg-amber-100 text-amber-700">{aiResults.pending} Pending</Badge>
                      </div>
                    )}
                    <Button
                      onClick={aiAnalyzeDocuments}
                      disabled={aiAnalyzing || matchedCount === 0}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {aiAnalyzing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing with AI...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Run AI Analysis
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Files list with matching */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {files.map(file => (
              <Card key={file.id} className={`transition-all ${
                file.case_id 
                  ? file.match_confidence >= 0.9 
                    ? 'border-green-300 bg-green-50/50' 
                    : file.match_confidence >= 0.7 
                      ? 'border-amber-300 bg-amber-50/50' 
                      : 'border-orange-300 bg-orange-50/50'
                  : 'border-red-300 bg-red-50/50'
              }`}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {getFileIcon(file.mime_type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium truncate">{file.original_filename}</p>
                          {getMatchBadge(file)}
                        </div>
                        
                        {file.case_id ? (
                          <div className="mt-2 p-2 bg-white rounded border flex items-center justify-between">
                            <div>
                              <p className="text-xs text-slate-500">Matched to:</p>
                              <p className="text-sm font-medium text-green-700">{file.employee_name}</p>
                              <p className="text-xs text-slate-500">Case: {file.case_id}...</p>
                              {file.check_type && (
                                <Badge variant="outline" className="mt-1 text-xs">{file.check_type}</Badge>
                              )}
                              {/* AI Analysis Result */}
                              {file.ai_verification_result && (
                                <div className="mt-2 pt-2 border-t">
                                  <div className="flex items-center gap-2">
                                    <Badge className={file.ai_verification_result === 'CLEAR' ? 'bg-green-100 text-green-700' : file.ai_verification_result === 'DISCREPANCY' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                      AI: {file.ai_verification_result}
                                    </Badge>
                                    {file.ai_confidence && (
                                      <span className="text-xs text-slate-500">
                                        ({Math.round(file.ai_confidence * 100)}% confidence)
                                      </span>
                                    )}
                                  </div>
                                  {file.ai_remarks && (
                                    <p className="text-xs text-slate-600 mt-1">{file.ai_remarks}</p>
                                  )}
                                </div>
                              )}
                            </div>
                            {file.match_confidence && file.match_confidence < 1.0 && (
                              <ConfidenceIndicator confidence={file.match_confidence} />
                            )}
                          </div>
                        ) : (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs text-amber-600">No automatic match found. Please match manually.</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {(!file.case_id || file.match_confidence < 0.7) && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" onClick={() => setSelectedFileForMatch(file)}>
                              <Link2 className="w-4 h-4 mr-1" />
                              {file.case_id ? 'Re-match' : 'Match'}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Match Document to Case</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label>File: {file.original_filename}</Label>
                              </div>
                              <div className="space-y-2">
                                <Label>Search Case by Employee Name or Case ID</Label>
                                <Input 
                                  placeholder="Start typing to search..."
                                  value={searchQuery}
                                  onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    searchCases(e.target.value);
                                  }}
                                />
                              </div>
                              
                              {searchResults.length > 0 && (
                                <div className="max-h-64 overflow-y-auto space-y-2">
                                  {searchResults.map(caseItem => (
                                    <div 
                                      key={caseItem.id} 
                                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                                      onClick={() => matchDocument(file.id, caseItem.id)}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium">{caseItem.employee_name}</p>
                                          <p className="text-xs text-slate-500">
                                            {caseItem.client_name} | Case: {caseItem.id}...
                                          </p>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {caseItem.checks?.slice(0, 3).map(check => (
                                              <Badge key={check.id} variant="outline" className="text-xs">
                                                {check.check_type}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                        <Button size="sm" variant="ghost">
                                          <Check className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => removeFile(file.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('upload')}>
              Back to Upload
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {aiResults && aiResults.clear + aiResults.discrepancy > 0 ? (
                <Button 
                  onClick={autoUpdateChecks}
                  disabled={autoUpdating}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {autoUpdating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Auto-Updating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Auto-Update {aiResults.clear + aiResults.discrepancy} Checks
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={commitUpload}
                  disabled={matchedCount === 0 || committing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {committing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Committing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Commit {matchedCount} Matched Documents
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Complete */}
      {step === 'complete' && (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-700 mb-2">Upload Complete!</h3>
          <p className="text-slate-600 mb-6">
            Successfully attached {matchedCount} documents to their respective cases.
          </p>
          <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
            Close
          </Button>
        </div>
      )}

      {/* Loading overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-center font-medium">Uploading files...</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BulkDocumentUpload;
